import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { PromoTimeLeft } from '../../../utils/dragonBoatPromo'

const pad = (value: number) => String(value).padStart(2, '0')

/**
 * A campaign clock that owns its own interval.
 *
 * The plan tab used to hold the ticking value in its own state, which meant every plan card,
 * every price and the whole hero were rebuilt once a second for the sake of one line of text.
 * Keeping the tick here confines the re-render to this span.
 *
 * `getTimeLeft` must be a stable reference (a module-level function from the campaign module,
 * not an inline arrow), or the interval is rebuilt on every parent render.
 */
const PromoCountdown = ({ getTimeLeft, className }: { getTimeLeft: () => PromoTimeLeft; className?: string }) => {
  const { t } = useTranslation('plan')
  const [timeLeft, setTimeLeft] = useState<PromoTimeLeft>(() => getTimeLeft())

  useEffect(() => {
    setTimeLeft(getTimeLeft())
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000)
    return () => clearInterval(timer)
  }, [getTimeLeft])

  return (
    <span className={className} role='timer' aria-live='off'>
      {t('promoCountdown', {
        days: pad(timeLeft.days),
        hours: pad(timeLeft.hours),
        minutes: pad(timeLeft.minutes),
        seconds: pad(timeLeft.seconds),
      })}
    </span>
  )
}

export default PromoCountdown
